/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import FeaturedSuggestion from 'bundles/cds-labs/components/chat/FeaturedSuggestion';
import type { SuggestionItem } from 'bundles/cds-labs/components/chat/FeaturedSuggestion';

import _t from 'i18n!nls/cds-labs';

export type { SuggestionItem };

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-400);
    align-items: center;
    justify-content: center;
    padding: var(--cds-spacing-200);
    width: 100%;
    height: 100%;
  `,

  heading: css`
    width: 100%;
  `,

  gradientName: css`
    background-image: linear-gradient(-59.5deg, var(--cds-color-blue-400) 5.78%, var(--cds-color-purple-750) 83.49%);
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    display: block;
    white-space: pre-wrap;
    word-break: break-word;
  `,

  headingMessage: css`
    color: var(--cds-color-neutral-primary);
    display: block;
    white-space: pre-wrap;
    word-break: break-word;
  `,

  suggestionList: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    align-items: flex-start;
    width: 100%;
    list-style: none;
    padding: 0;
    margin: 0;
  `,

  listItem: css`
    width: 100%;
  `,
};

export type ChatEmptyStateProps = {
  /** Name of the user shown in the personalised greeting. Omit to show a generic greeting. */
  userName?: string;
  /** Prompt displayed below the greeting. */
  message?: string;
  /** List of suggestion cards to render. */
  suggestions: SuggestionItem[];
};

const ChatEmptyState = ({ userName, message, suggestions }: ChatEmptyStateProps) => {
  const resolvedMessage = message ?? _t('How can I help?');
  const greeting = userName ? _t('Hi, #{userName}.', { userName }) : _t('Hello.');

  return (
    <div css={styles.root} data-testid="ChatEmptyState">
      <div css={styles.heading}>
        <Typography2 variant="titleSmall" component="h2" css={styles.gradientName}>
          {greeting}
        </Typography2>
        <Typography2 variant="titleSmall" component="p" css={styles.headingMessage}>
          {resolvedMessage}
        </Typography2>
      </div>

      <ul css={styles.suggestionList} aria-label={_t('Suggestions')}>
        {suggestions.map((suggestion) => (
          <li key={suggestion.id} css={styles.listItem}>
            <FeaturedSuggestion suggestion={suggestion} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ChatEmptyState;
